import get from 'lodash.get';
import { ofType } from 'redux-observable';
import { EMPTY, of, concat, merge, race, timer, Subject, BehaviorSubject } from 'rxjs';
import {
  catchError,
  debounceTime,
  finalize,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
  buffer,
  filter,
  scan,
  shareReplay,
  distinctUntilChanged,
} from 'rxjs/operators';
import LogRocket from 'logrocket';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { CSWTypes, CSWTypeValues } from '../../enums/csw-types';
import * as PrintActions from '../../store/printing/print.actions';
import * as UserActions from '../../store/user/user.actions';
import * as SellerActions from '../../store/seller/seller.actions';
import * as RateViewerActions from '../../store/rate-viewer/rate-viewer.actions';
import * as MarkAsShippedActions from '../../store/mark-as-shipped/mark-as-shipped.actions';
import * as JobPipelineActions from '../../store/job-pipeline/job-pipeline.actions';

import { FulfillmentPlanOrderGridStatus } from '../../enums/fulfillmentplan-order-grid-status';
import { InsuranceValues } from '../../enums/insurance-values';
import { fulfillmentPlanToCSW } from './csw.fulfillment-plan-to-csw';
import { DHLTermsAndConditionsService } from '../../services/dhl-terms-and-conditions-service';
import {
  ALL_DEFAULT_OPTIONS,
  cleanOptionsBasedOnSelectedServiceId,
  reassignDGToOptionsOnSelectedService,
} from '../../services/other-shipping-options/constants';

import {
  rateRefreshSubject,
  createLabelSubject,
  serviceCollectionSubject,
  availableSellerShippingServicesSubject,
} from '../../fulfillment-planner/context';
import { updateFulfillmentPlanOrderGridStatus } from '../../fulfillment-plans/actions';
import { OtherShippingOptionsService } from '../../services/other-shipping-options/other-shipping-options-service';
import { ShipmentConfigurationService } from '../../services/shipping-configuration-service';
import * as BulkActions from '../../store/bulk/bulk.actions';
import * as BulkloadActions from '../../store/bulkload/bulkload.actions';
import * as BulkApi from '../../store/bulk/bulk.api';
import * as EntityActions from '../../store/entities/entities.actions';
import Global from '../../store/global-selectors';
import { setSelectedReturnShipment, goToOrderDetails } from '../orders/order-details/orders.details.actions';
import { getQuickshipPropsFromState } from '../quickship/quickship.helpers';
import * as Actions from './csw.actions';
import * as Api from './csw.api';
import * as Helpers from './csw.helpers';
import * as Builders from './csw.request-builders';
import { FeatureFlags } from '../../feature-flags/feature-flags';
import { getInitialReturnPackageShippingConfiguration } from '../../fulfillment-planner/helpers/get-return-package-object';
import { getReturnServiceShippingConfiguration } from '../../fulfillment-planner/helpers/get-return-service';
import { getIsServiceLocalDelivery } from '../../hooks/use-is-service-local-delivery';
import { hasUserAcceptedInsuranceTerms } from '../../services/insurance-terms-service';
import { launchShipsuranceTermsModalWaitToProceed } from '../../services/insurance-terms-helper';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { PackageService } from '../../services/package-service';
import { LogService } from '../../services/log-service';
import * as BulkUpdateActions from '../bulk/update/bulk.update.actions';
import { RATE_BROWSER } from '../../constants/fulfillment-plan-ids';
import { bulkLoadSalesOrders$ } from '../bulkload/bulkload.observables';
import { getActualSalesOrderIds } from '../orders/orders.helpers';
import { noop } from 'ui-components/default-props/noop';

const handleDidCreateExternalFulfillmentForBatchId = ({ batchId = '', batchesFromLabelQue = [] }) => {
  const batchMatch = batchesFromLabelQue.find((batchObj) => batchObj.batchId === String(batchId));
  const { fulfillmentPlanId, errors, isComplete } = batchMatch ?? {};
  const didCreationSucceedWithoutError = !errors && isComplete;

  // Notify the FulfillmentPlannerContext that the LabelQue completed creating the label:
  createLabelSubject.next({
    fulfillmentPlanId,
    isCreatingLabel: false,
    didCreateExternalFulfillment: didCreationSucceedWithoutError,
    errorMessages: [],
  });
  /* TODO: The above "errorMessage: []" indicates that we don't currently have error handling when errors
  // occur during the createExternalFulfillment process. Future work is needed (SPD-8951) to determine
  // user-friendly error messages and ensure they are correctly emitted by the createLabelSubject above.
  //
  // It appears there's a pattern to do exactly this in the labelPurchasePusherRace function found in
  // 'bulk.label.observables.js'  We should confirm this pattern will work fulfillments since that looks
  // to be a label creation implementation.  - DAT 2/3/22
  */
};

const getCswTypeId = (state, serviceId, initCswTypeId) => {
  const isLocalDelivery = getIsServiceLocalDelivery(state, serviceId);
  if (initCswTypeId && initCswTypeId !== CSWTypes.standard.id) {
    return initCswTypeId;
  }
  if (isLocalDelivery) {
    return CSWTypes.localDelivery.id;
  }
  return CSWTypes.standard.id;
};

export const initCsw = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_INIT_SHIPMENT_CONFIGURATION),
    mergeMap(({ fulfillmentPlanId, cswTypeId }) => {
      const state = getState();
      const accountPackages = Global.selectSellerPackages(state) || [];
      const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, {
        fulfillmentPlanId,
      });
      const CSWShippingConfiguration = Global.selectCSWShippingConfiguration(state, { fulfillmentPlanId });
      const updatePayload = fulfillmentPlanToCSW(
        fulfillmentPlan,
        accountPackages,
        CSWShippingConfiguration,
        Actions.CSW_INIT_SHIPMENT_CONFIGURATION,
        state,
      );
      return of(
        Actions.setupShipmentConfiguration(fulfillmentPlanId, updatePayload, {
          type: getCswTypeId(state, updatePayload?.serviceId, cswTypeId),
        }),
      );
    }),
  );
};

export const createLabel = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_CREATE_LABEL),
    throttleTime(500),
    switchMap(({ fulfillmentPlanId, autoPrint, doNotPrint }) => {
      const state = getState();
      const fp = Global.selectEntitiesFulfillmentPlanById(state, {
        fulfillmentPlanId,
      });
      if (!fp) {
        return EMPTY;
      }

      const shouldShowShipstationTerms = DHLTermsAndConditionsService.sellerHasDhlWalletPurchaseAndNotAcceptedTerms(state, [fp]);
      if (shouldShowShipstationTerms) {
        const postAcceptanceAction = Actions.createLabel(fulfillmentPlanId, autoPrint, doNotPrint);
        return of(SellerActions.promptShipstationDHLTermsAndConditions(postAcceptanceAction));
      }

      const insuranceProviderName = Global.selectEntitiesFulfillmentPlanProperty(state, {
        fulfillmentPlanId,
        property: 'insuranceProvider',
      });
      const selectedInsuranceOption =
        Object.values(InsuranceProviders).find((insuranceOption) => insuranceOption.name === insuranceProviderName) ||
        InsuranceProviders.None;
      const shouldShowShipsuranceTerms = !hasUserAcceptedInsuranceTerms(state, selectedInsuranceOption.insuranceProviderId);
      if (shouldShowShipsuranceTerms) {
        return launchShipsuranceTermsModalWaitToProceed(
          action$,
          Actions.createLabel(fulfillmentPlanId, autoPrint, doNotPrint),
          selectedInsuranceOption.insuranceProviderId,
        );
      }

      createLabelSubject.next({ fulfillmentPlanId, isCreatingLabel: true });
      const labelObject = Builders.buildCreateLabelRequest(state, {
        fulfillmentPlanId,
      });
      const shipDate = labelObject.shipDate ? new Date(labelObject.shipDate) : new Date();
      const shipDateAsUTCMidnight = new Date(Date.UTC(shipDate.getFullYear(), shipDate.getMonth(), shipDate.getDate()));

      const createLabelProps = {
        fulfillmentPlanId,
        autoPrint,
        defaultPrinterAutoPrintSetting: !!Global.selectPrintSettingsLabelPrintSettings(state),
        doNotPrint,
        labelObject,
        saveConfigAndRate: Builders.buildSaveShippingConfigurationAndRateRequest(state, {
          fulfillmentPlanId,
        }),
        state,
        shipDate: shipDateAsUTCMidnight,
        isPersonalShipsuranceAccount: Global.selectSellerPersonalShipsuranceAccount(state),
        labelQue: Global.selectLabelQueBatches(state),
      };
      return Helpers.handleCreateLabelRequest(createLabelProps, action$, getState);
    }),
  );
};

export const createLabelFailure = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_CREATE_LABEL_FAILURE),
    mergeMap(({ fulfillmentPlanId, errorMessages, isLabelPurchaseComplete }) => {
      createLabelSubject.next({
        fulfillmentPlanId,
        isCreatingLabel: !isLabelPurchaseComplete,
        errorMessages,
      });
      const state = getState();
      const [salesOrderId] = Global.selectEntitiesSalesOrderIdsByFulfillmentPlanIds(state, {
        fulfillmentPlanIds: [fulfillmentPlanId],
      });
      const quickshipProps = getQuickshipPropsFromState(state);
      const { shouldUseQuickship: didUseQuickship } = quickshipProps;
      const { showErrorsOrWarnings } = Global.selectUserQuickshipShouldShowErrorsOrWarnings(state);
      const isFromRateBrowser = fulfillmentPlanId === RATE_BROWSER;
      const isGridSidebarVisible = Global.selectOrdersGridisSidebarVisible(state);
      const hasQuickshipError = didUseQuickship && showErrorsOrWarnings;
      const showOrderDetails = !isFromRateBrowser && !isGridSidebarVisible && (!didUseQuickship || hasQuickshipError);
      return showOrderDetails ? of(goToOrderDetails({ salesOrderId, fulfillmentPlanId })) : EMPTY;
    }),
  );
};

export const openPrintModalFromLabelSuccessEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.CSW_CREATE_LABEL_SUCCESS),
    mergeMap((action) => {
      const { doNotPrint, sourceHasHandled } = action;
      if (sourceHasHandled) {
        return EMPTY;
      }
      const state = getState();
      const fulfillmentPlanId = get(action, 'labelObject.fulfillmentPlanId');
      if (doNotPrint) {
        createLabelSubject.next({
          fulfillmentPlanId,
          isCreatingLabel: false,
          errorMessages: [],
        });
        // check if we are in s2p mode
        const routerPath = history?.location?.pathname;
        const scanToPrintMode = routerPath && routerPath.indexOf('scan') > -1;
        if (scanToPrintMode) {
          const jobId = Global.selectScanToPrintJobId(state);
          return of(JobPipelineActions.goForwardOnJob(jobId));
        }
        return of(
          updateFulfillmentPlanOrderGridStatus({
            fulfillmentPlanId,
            orderGridStatus: FulfillmentPlanOrderGridStatus.Shipped.name,
          }),
        );
      }

      const fulfillmentPlanIds = [get(action, 'labelObject.fulfillmentPlanId')];
      return BulkApi.fulfillmentPlanToFulfillment(fulfillmentPlanIds).pipe(
        mergeMap((data = emptyArray) => {
          const fetchedFulfillmentIds = data.map((row) => get(row, 'fulfillmentId'));
          if (!fetchedFulfillmentIds.length) {
            return EMPTY;
          }
          const defaultPrinterAutoPrintSetting = !!Global.selectPrintSettingsLabelPrintSettings(state);
          return concat(
            of(PrintActions.printLabel(fulfillmentPlanIds, fetchedFulfillmentIds, false, false, defaultPrinterAutoPrintSetting)),
            of(
              updateFulfillmentPlanOrderGridStatus({
                fulfillmentPlanId: fulfillmentPlanIds[0],
                orderGridStatus: FulfillmentPlanOrderGridStatus.Shipped.name,
              }),
            ),
          );
        }),
        catchError(() => EMPTY),
        finalize(() => {
          createLabelSubject.next({
            fulfillmentPlanId,
            isCreatingLabel: false,
            errorMessages: [],
          });
        }),
      );
    }),
  );
};

export const changeInsuranceValue = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_CHANGE_INSURED_VALUE),
    mergeMap((action) => {
      const state = getState();
      const { index, insuranceValue, fulfillmentPlanId } = action;
      // / TODO: revisit this as editing sales order shipTo does not modify FPs shipTo and later when FPs can have 1+ salesOrders
      const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, {
        fulfillmentPlanId,
      });
      const salesOrderId = get(fulfillmentPlan, 'salesOrdersFulfilledIds', emptyArray)[0];
      const ammountSummary = Global.selectEntitiesSalesOrderProperty(state, {
        salesOrderId,
        property: 'amountSummary',
      });
      const sellerDefaultCurrencyCode = Global.selectSellerDefaultCurrency(state);
      const orderTotal = get(ammountSummary, 'orderTotal.value', 0);
      const shippingPaid = get(ammountSummary, 'shippingPaid.value', 0);
      const fulfillmentPlanItems = get(fulfillmentPlan, 'items') || emptyArray;
      const itemValue = fulfillmentPlanItems.reduce((sum, i) => sum + get(i, 'totalPrice.value', 0), 0);
      const updateAction = (insuredValue) => {
        return Actions.updateShipmentConfigurationPackages(
          fulfillmentPlanId,
          {
            insuredValue: {
              value: insuredValue,
              code: sellerDefaultCurrencyCode,
            },
          },
          index,
          true,
        );
      };

      if (insuranceValue === InsuranceValues.ThirtyPercent.name) {
        return of(updateAction(orderTotal * 0.3));
      }

      if (insuranceValue === InsuranceValues.OrderCost.name) {
        const value = orderTotal - shippingPaid;
        return of(updateAction(value > 0 ? value : 0));
      }

      if (insuranceValue === InsuranceValues.Sum.name) {
        return of(updateAction(itemValue));
      }
      return of(updateAction(orderTotal));
    }),
  );
};

const buildContextPayloadSafe = (cswType, payload, getState) => {
  try {
    return cswType.getShippingConfigurationForPayload(payload, getState());
  } catch (e) {
    LogRocket.track('Error building rating payload', { error: e?.message });
    LogService.logInfo('Error building rating payload', {
      errorMessage: e?.message,
      stack: e?.stack,
      payload,
    });
    return null;
  }
};

export const getRateEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_GET_RATE_WITH_PAYLOAD),
    debounceTime(1000),
    switchMap((action) => {
      const { fulfillmentPlanId, payload, cswTypeId } = action;

      if (!fulfillmentPlanId) {
        throw new Error(`${action.type}: no fulfillmentPlanId in getRateEpic`);
      }
      const cswType = CSWTypeValues.find((t) => t.id === cswTypeId) || CSWTypes.rateBrowser;

      const { enableCswRatePayloadDiff } = new FeatureFlags({
        state: getState(),
      });

      rateRefreshSubject.next({ fulfillmentPlanId, isRefreshingRate: true });
      return cswType.getRateAction$({
        fulfillmentPlanId,
        onSuccess: () => {
          return EMPTY;
        },
        onError: () => {
          return EMPTY;
        },
        action$,
        getState,
        shippingConfiguration: buildContextPayloadSafe(cswType, { fulfillmentPlanId, ...payload }, getState),
        cswTypeId,
        enableCswRatePayloadDiff,
      });
    }),
  );
};

export const saveShippingConfigEpic = (action$, { getState }) => {
  const bufferFlush$ = new Subject().pipe(debounceTime(1000));

  const pendingSyncActions$ = new Subject().pipe(
    scan((acc, actions) => {
      if (!actions) {
        return [];
      }
      return [...acc, ...actions];
    }, []),
    shareReplay(1),
  );
  pendingSyncActions$.subscribe(noop);

  const activeBulkloads = new Map();
  const bulkloadEmitter$ = new Subject();

  const syncServiceCollectionFromBulkloadAction = (fulfillmentPlanId, bulkloadAction) => {
    const fulfillmentPlan = bulkloadAction?.entities?.fulfillmentPlans?.[fulfillmentPlanId];
    if (!fulfillmentPlan?.labelConfiguration?.serviceCollectionGuid) {
      return;
    }

    const { options, packages, sellerLabelProviderId, serviceCollectionGuid, serviceId: serviceIdInt } = fulfillmentPlan.labelConfiguration;

    const serviceId = serviceIdInt?.toString();
    const service = Global.selectSellerServiceById(getState(), { serviceId });

    serviceCollectionSubject.next({
      fulfillmentPlanId,
      carrierId: service?.carrierId,
      confirmationId: options?.confirmationEnumId?.toString(),
      packageTypeId: packages?.[0]?.packageTypeId?.toString(),
      sellerLabelProviderId: sellerLabelProviderId?.toString(),
      serviceId,
      serviceCollectionGuid,
      packages,
    });
  };

  const startBulkload = ({ salesOrderIds, onBulkloadSuccess = noop }) => {
    return bulkLoadSalesOrders$(salesOrderIds).pipe(
      switchMap((successAction) => {
        onBulkloadSuccess(successAction);
        return concat(
          of(successAction, EntityActions.bulkRequestEntitiesSuccess({ salesOrderIds })),
          pendingSyncActions$.pipe(
            take(1),
            switchMap((allActions) => {
              return of(
                ...allActions.map((a) => {
                  return a.meta.onFlush();
                }),
              );
            }),
            finalize(() => {
              pendingSyncActions$.next(null);
              activeBulkloads.delete(JSON.stringify(salesOrderIds));
            }),
          ),
        );
      }),
      catchError(() => {
        return EMPTY;
      }),
    );
  };

  return merge(
    bulkloadEmitter$,
    action$.pipe(
      ofType(Actions.CSW_SAVE_CONFIG_WITH_PAYLOAD),
      tap((act) => bufferFlush$.next(act)),
      buffer(bufferFlush$),
      filter((actions) => !!actions.length),
      switchMap((actions) => {
        pendingSyncActions$.next(actions);
        const action = actions[actions.length - 1];
        const analyticsAction = actions.find((a) => a.meta.sendAnalytics);

        const { fulfillmentPlanId, isEnteringExternalFulfillment, meta, payload, cswTypeId, doNotCallForRate } = action;

        if (!fulfillmentPlanId) {
          throw new Error(`${action.type}: no fulfillmentPlanId in saveShippingConfigEpic`);
        }

        if (doNotCallForRate) {
          return EMPTY;
        }

        const cswType = CSWTypeValues.find((t) => t.id === cswTypeId) || CSWTypes.standard;

        const { enableCswRatePayloadDiff } = new FeatureFlags({
          state: getState(),
        });
        if (isEnteringExternalFulfillment) {
          const delay$ = timer(4000);
          const ofType$ = action$.pipe(
            ofType(
              Actions.CSW_UPDATE_SHIPMENT_CONFIGURATION,
              Actions.CSW_UPDATE_SHIPMENT_CONFIGURATION_OPTIONS,
              Actions.CSW_UPDATE_SHIPMENT_CONFIGURATION_PACKAGES,
              Actions.CSW_UPDATE_SHIPMENT_CONFIGURATION_PACKAGES_FROM_PRESET,
              Actions.CSW_SAVE_MULTIPACKAGE_SET,
            ),
            take(1),
          );
          return concat(
            race(delay$, ofType$).pipe(
              tap(() =>
                rateRefreshSubject.next({
                  fulfillmentPlanId,
                  isRefreshingRate: true,
                }),
              ),
              switchMap(() => EMPTY),
            ),
            cswType.getRateAction$({
              fulfillmentPlanId,
              onSuccess: ({ updateAction }) => of({ fulfillmentPlanId, updateActions: [updateAction] }),
              onError: ({ updateAction, errorMessage }) =>
                of({
                  fulfillmentPlanId,
                  updateActions: [updateAction, meta.onError(errorMessage)],
                }),
              action$,
              getState,
              shippingConfiguration: buildContextPayloadSafe(cswType, { fulfillmentPlanId, ...payload }, getState),
              cswTypeId,
              enableCswRatePayloadDiff,
            }),
          );
        }

        rateRefreshSubject.next({ fulfillmentPlanId, isRefreshingRate: true });
        return cswType.getRateAction$({
          fulfillmentPlanId,
          onSuccess: ({ updateAction, shouldSkipBulkLoad }) => {
            const sendAnalytics = analyticsAction?.meta?.sendAnalytics;
            if (sendAnalytics) {
              sendAnalytics();
            }
            return of({
              fulfillmentPlanId,
              updateActions: [updateAction],
              shouldSkipBulkLoad,
            });
          },
          onError: ({ updateAction, errorMessage }) => {
            return of({
              fulfillmentPlanId,
              updateActions: [updateAction, meta.onError(errorMessage)],
            });
          },
          action$,
          getState,
          shippingConfiguration: buildContextPayloadSafe(cswType, { fulfillmentPlanId, ...payload }, getState),
          cswTypeId,
          enableCswRatePayloadDiff,
        });
      }),
      mergeMap(({ fulfillmentPlanId, updateActions, shouldSkipBulkLoad }) => {
        const actions = [...updateActions];

        if (!shouldSkipBulkLoad) {
          const salesOrderIds = getActualSalesOrderIds(getState(), [fulfillmentPlanId]);

          const stringIds = JSON.stringify(salesOrderIds);
          if (activeBulkloads.has(stringIds)) {
            activeBulkloads.get(stringIds).unsubscribe();
          }

          const onBulkloadSuccess = (bulkloadAction) => {
            const { enableSaveShippingConfigServiceCollectionSync } = new FeatureFlags({ state: getState() });
            if (enableSaveShippingConfigServiceCollectionSync) {
              syncServiceCollectionFromBulkloadAction(fulfillmentPlanId, bulkloadAction);
            }
          };

          activeBulkloads.set(
            stringIds,
            startBulkload({
              salesOrderIds,
              onBulkloadSuccess,
            }).subscribe((next) => {
              bulkloadEmitter$.next(next);
            }),
          );

          actions.push(EntityActions.startSalesOrderLoading(salesOrderIds));
        }

        return concat(
          of(...actions),
          !shouldSkipBulkLoad
            ? EMPTY
            : pendingSyncActions$.pipe(
                take(1),
                switchMap((allActions) => {
                  return of(
                    ...allActions.map((a) => {
                      return a.meta.onFlush();
                    }),
                  );
                }),
                finalize(() => {
                  pendingSyncActions$.next(null);
                }),
              ),
        );
      }),
    ),
  );
};

const shouldCompareRates = (state, fulfillmentPlanId, shippingConfiguration) => {
  if (!shippingConfiguration.serviceId) {
    return false;
  }
  const isLocalDelivery = getIsServiceLocalDelivery(state, shippingConfiguration.serviceId);
  if (isLocalDelivery) {
    return false;
  }

  const isExternalFulfillment = !!shippingConfiguration.sellerFulfillmentProviderId;
  if (isExternalFulfillment) {
    return false;
  }

  const rateSummary = Global.selectEntitiesFulfillmentPlanProperty(state, {
    fulfillmentPlanId,
    property: 'rateSummary',
  });
  if (!!rateSummary?.rate && rateSummary.rate.totalCost.value === 0) {
    return false;
  }

  return true;
};

export const cswCompareRates = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_COMPARE_RATES),
    switchMap(() => {
      return action$.pipe(
        ofType(Actions.CSW_SETUP_SHIPMENT_CONFIGURATION),
        // prevent duplicate actions from causing multiple executions of the below logic
        distinctUntilChanged((prev, curr) => {
          const currentfulfillmentPlanId = curr.fulfillmentPlanId;
          const prevfulfillmentPlanId = prev.fulfillmentPlanId;
          if (!currentfulfillmentPlanId) {
            return false;
          }
          return currentfulfillmentPlanId === prevfulfillmentPlanId;
        }),
        switchMap(({ fulfillmentPlanId }) => {
          if (!Number.parseInt(fulfillmentPlanId, 10)) {
            return EMPTY;
          }
          const { enableCompareRateOnLoad } = new FeatureFlags({
            state: getState(),
          });
          const { compareRate = false, debounceMs = 0 } = enableCompareRateOnLoad;

          if (!compareRate || !Number.parseInt(debounceMs, 10)) {
            return EMPTY;
          }

          const currentValueSubject = new BehaviorSubject(null);
          return currentValueSubject.pipe(
            debounceTime(debounceMs),
            takeUntil(action$.pipe(ofType(Actions.CSW_SAVE_CONFIG_WITH_PAYLOAD, take(1)))),
            mergeMap(() => {
              const state = getState();
              const shippingConfiguration = Global.selectCSWShippingConfiguration(state, {
                fulfillmentPlanId,
              });
              const canCompareRate = shouldCompareRates(state, fulfillmentPlanId, shippingConfiguration);
              if (!canCompareRate) {
                return EMPTY;
              }

              const cswType = CSWTypes.standard;
              rateRefreshSubject.next({
                fulfillmentPlanId,
                isRefreshingRate: true,
              });

              return cswType.getRateAction$({
                fulfillmentPlanId,
                onSuccess: ({ updateAction }) => {
                  return of(updateAction);
                },
                onError: () => {
                  return EMPTY;
                },
                action$,
                getState,
                shippingConfiguration: buildContextPayloadSafe(cswType, { fulfillmentPlanId, ...shippingConfiguration }, getState),
                cswTypeId: cswType.id,
                enableCswRatePayloadDiff: false,
                shouldSkipSave: true,
              });
            }),
          );
        }),
      );
    }),
  );
};

const checkCanRetrieveCheapestRate = (fulfillmentPlanId, getState) => {
  const { isCheapestRateEnabled } = new FeatureFlags({ state: getState() });
  if (!isCheapestRateEnabled) {
    return false;
  }

  const shippingConfiguration = Global.selectCSWShippingConfiguration(getState(), { fulfillmentPlanId });
  const hasPackageSelected = !!shippingConfiguration?.packages?.find((p) => p?.packageTypeId);
  if (!hasPackageSelected) {
    return false;
  }

  const hasWeight = shippingConfiguration.packages.reduce((acc, cur) => {
    return acc + (cur?.weight?.value || 0);
  }, 0);
  if (!hasWeight) {
    return false;
  }

  return true;
};

export const getBestWayToShip = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_GET_RATE_WITH_PAYLOAD, Actions.CSW_SAVE_CONFIG_WITH_PAYLOAD, Actions.CSW_GET_BEST_WAY_TO_SHIP_ON_MOUNT),
    switchMap(({ fulfillmentPlanId, payload }) => {
      // rate browser / customer create label do not ever show best way to ship so we bail early for those
      if (!payload.setBestWayToShip) {
        return EMPTY;
      }

      const canRetrieveCheapestRate = checkCanRetrieveCheapestRate(fulfillmentPlanId, getState);
      if (!canRetrieveCheapestRate) {
        return EMPTY;
      }

      const { setBestWayToShip } = payload;
      setBestWayToShip({ isLoading: true });

      return of(null).pipe(
        debounceTime(300),
        mergeMap(() => {
          const shippingConfiguration = Global.selectCSWShippingConfiguration(getState(), { fulfillmentPlanId });
          return Api.getBestWayToShip(shippingConfiguration).pipe(
            switchMap((response) => {
              setBestWayToShip({ isLoading: false, ...response });
              return EMPTY;
            }),
            catchError(() => {
              setBestWayToShip({ isLoading: false, hasError: true });
              return EMPTY;
            }),
          );
        }),
      );
    }),
  );
};

const updatePackagesAfterServiceChange = (fulfillmentPlanId, service, state) => {
  const cswPackages = Global.selectCSWPackages(state, { fulfillmentPlanId });
  const { supportsMultiPackage = false } = get(service, 'features', {});
  if (cswPackages.length > 1 && !supportsMultiPackage) {
    const combinedPackage = PackageService.combineMultiPackages(cswPackages);
    return [combinedPackage];
  }
  return cswPackages;
};

export const changeServiceAndOtherShippingOptions = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_UPDATE_SERVICE_AND_OTHER_SHIPPING_OPTIONS),
    mergeMap(({ fulfillmentPlanId, serviceId, sellerLabelProviderId }) => {
      const state = getState();
      const shippingOptions = Global.selectCSWOptions(state, { fulfillmentPlanId }) || emptyObject;
      const services = Global.selectSellerServices(state).filter((service) => !service.isHidden);
      const service = services.find((s) => serviceId === s.serviceId);
      const optionCodes = get(service, 'optionCodes', emptyArray).concat(ALL_DEFAULT_OPTIONS);
      const carrierCleanedOptions = OtherShippingOptionsService.cleanOptionsAfterServiceChange(optionCodes, shippingOptions);
      const serviceCleanedOptions = cleanOptionsBasedOnSelectedServiceId(serviceId, carrierCleanedOptions);
      const optionsWithReassignedDG = reassignDGToOptionsOnSelectedService(service?.optionCodes ?? [], serviceCleanedOptions);
      const payload = {
        serviceId,
        sellerLabelProviderId,
        options: optionsWithReassignedDG,
        packages: updatePackagesAfterServiceChange(fulfillmentPlanId, service, state),
      };

      return of(Actions.updateShipmentConfiguration(fulfillmentPlanId, { ...payload }));
    }),
  );
};

export const enterReturnsMode = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_ENTER_RETURNS_MODE),
    mergeMap(({ salesOrderId, shipmentItemsForReturn = emptyArray, shipTo }) => {
      const state = getState();
      const fulfillmentPlanId = Helpers.getCswReturnsFulfillmentPlanId(salesOrderId);
      const items = shipmentItemsForReturn.map((i) => ({
        ...i,
        selected: true,
      }));
      const outGoingShipmentId = Global.selectOrderDetailsShipmentIdForReturns(state);
      const outgoingfulfillmentPlanShipFromId = Global.selectEntitiesFulfillmentPlanProperty(state, {
        fulfillmentPlanId: outGoingShipmentId,
        property: 'shipFromId',
      });
      const currentShipTo =
        shipTo ||
        Global.selectEntitiesSalesOrderProperty(state, {
          salesOrderId,
          property: 'shipTos.0',
        }) ||
        {};
      const salesOrderStore = Global.selectEntitiesSalesOrderProperty(state, {
        salesOrderId,
        property: 'store',
      });
      const marketplaceId = salesOrderStore?.marketplaceId;

      const initialShippingConfig = {
        ...getReturnServiceShippingConfiguration(state, salesOrderId),
        packages: getInitialReturnPackageShippingConfiguration(state),
        shipFromId: outgoingfulfillmentPlanShipFromId || Global.selectSellerDefaultShipFromId(state),
        shipTo: currentShipTo,
        options: {
          confirmationEnumId: 0,
        },
        marketplaceId,
      };
      const otherConfiguration = {
        isReturn: true,
        isAutoRMA: true,
        type: CSWTypes.returns.id,
        salesOrderId,
        overrideShipment: {
          items,
        },
      };

      const createReturnObservable = (availableSellerShippingServices) => {
        if (availableSellerShippingServices) {
          initialShippingConfig.availableSellerShippingServices = availableSellerShippingServices;
          availableSellerShippingServicesSubject.next({
            fulfillmentPlanId,
            shippingServices: availableSellerShippingServices,
          });
        }
        return concat(
          of(setSelectedReturnShipment(fulfillmentPlanId)),
          of(Actions.setupShipmentConfiguration(fulfillmentPlanId, initialShippingConfig, otherConfiguration)),
        );
      };

      return Api.getAvailableShippingServices(initialShippingConfig).pipe(
        mergeMap((data) => {
          return createReturnObservable(get(data, 'data'));
        }),
        catchError(() => createReturnObservable()),
      );
    }),
  );
};

const buildEmptyRateObject = (state) => {
  const sellerCurrency = Global.selectSellerDefaultCurrency(state);
  return {
    shippingCost: {
      value: 0,
      code: sellerCurrency,
    },
    insuranceCost: {
      value: 0,
      code: sellerCurrency,
    },
    confirmationCost: {
      value: 0,
      code: sellerCurrency,
    },
    otherCost: {
      value: 0,
      code: sellerCurrency,
    },
    totalCost: {
      value: 0,
      code: sellerCurrency,
    },
    estimatedDelivery: '',
  };
};

export const enterExternalFulfillmentMode = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_ENTER_EXTERNAL_FULFILLMENT_MODE),
    mergeMap(({ fulfillmentPlanId }) => {
      return concat(
        of(EntityActions.entitiesPatchRates(fulfillmentPlanId, buildEmptyRateObject(getState()))),
        of(
          Actions.setupShipmentConfiguration(fulfillmentPlanId, emptyObject, {
            isExternalFulfillment: true,
            type: CSWTypes.externalFulfillments.id,
          }),
        ),
      );
    }),
  );
};

export const createExternalFulfillment = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_SEND_EXTERNAL_FULFILLMENT),
    switchMap((action) => {
      const { fulfillmentPlanId } = action;

      if (!fulfillmentPlanId) {
        throw new Error(`${action.type}: no fulfillmentPlanId in createExternalFulfillment`);
      }

      createLabelSubject.next({ fulfillmentPlanId, isCreatingLabel: true });
      const notificationId = Helpers.showSendingExternalFulfillment();
      const metadata = {
        fulfillmentPlanId,
        autoPrint: false,
        doNotPrint: true,
        notificationId,
        fromSingleLabel: true,
        isExternalFulfillment: true,
      };

      return BulkApi.createLabel([fulfillmentPlanId]).pipe(
        mergeMap((response) => {
          const labelBatchIds = get(response, 'labelBatchIds', emptyArray);
          return merge(
            of(
              Actions.sendExternalFulfillmentSuccess(fulfillmentPlanId, notificationId),
              BulkActions.bulkPurchaseLabelSuccess([fulfillmentPlanId], undefined, undefined, labelBatchIds, false, metadata),
              MarkAsShippedActions.markShippedSuccess({
                batchIds: labelBatchIds,
              }),
            ),
            // creates a listener for the completion of the creation process - as of 2/3/22 this is handled via Pusher message, DAT
            action$.pipe(
              ofType(
                MarkAsShippedActions.MARK_SHIPPED_PUSHER_MESSAGE,
                BulkActions.BULK_PROCESSING_CREATE_LABEL_FINAL_MESSAGE,
                BulkActions.BULK_PROCESSING_FULFILLMENT_UNKNOWN_ERROR,
              ),
              throttleTime(1000),
              take(1),
              mergeMap((successAction) => {
                const { batchId } = successAction;
                const state = getState();
                const batchesFromLabelQue = Global.selectLabelQueBatches(state);
                handleDidCreateExternalFulfillmentForBatchId({
                  batchId,
                  batchesFromLabelQue,
                });

                return EMPTY;
              }),
            ),
          );
        }),
        catchError((errorResponse) => {
          const errorMessages = get(errorResponse, 'response.data.Errors', emptyArray).map((e) => e.Message);
          createLabelSubject.next({
            fulfillmentPlanId,
            isCreatingLabel: false,
            didCreateExternalFulfillment: false,
            errorMessages,
          });

          return EMPTY;
          // TODO: handle error case
        }),
      );
    }),
  );
};

export const showRateViewerErrors = (action$, { getState }) => {
  return action$.pipe(
    ofType(RateViewerActions.RATE_VIEWER_START_LOADING_RATES_ERROR),
    mergeMap(({ rateViewerKey: fulfillmentPlanId, errors }) => {
      const shippingConfiguration = Global.selectCSWShippingConfiguration(getState(), { fulfillmentPlanId });
      if (!shippingConfiguration) {
        return EMPTY;
      }

      const updateCswFailure$ = of(Actions.updateFulfillmentPlanAndRateFailure(fulfillmentPlanId, errors));

      const clearRateViewer$ = of(RateViewerActions.clearRateViewer(fulfillmentPlanId));
      return concat(updateCswFailure$, clearRateViewer$);
    }),
  );
};

export const saveShippingDropdownPreferenceToUserSettingsEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.CSW_SET_SHIPPING_DROPDOWN_PREFERENCE),
    mergeMap((action) => {
      const setting = {
        settingName: 'ShippingServiceGrouping',
        settingValue: action.preference,
        excludeV3prefix: true,
      };
      return of(UserActions.saveUserSetting(setting, true));
    }),
  );
};

export const startUpdateAvailableShippingServices = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CSW_START_UPDATE_AVAILABLE_SHIPPING_SERVICES),
    mergeMap(({ fulfillmentPlanId, updatedShipFromId }) => {
      const state = getState();
      if (!updatedShipFromId || (updatedShipFromId && updatedShipFromId.includes('external'))) {
        return EMPTY;
      }

      const salesOrderId =
        Global.selectEntitiesSalesOrderIdsByFulfillmentPlanIds(state, {
          fulfillmentPlanIds: [fulfillmentPlanId],
        }) || [];
      const requestedService = Global.selectEntitiesSalesOrderProperty(state, {
        salesOrderId: salesOrderId[0],
        property: 'requestedService',
      });
      const salesOrderStore = Global.selectEntitiesSalesOrderProperty(state, {
        salesOrderId: salesOrderId[0],
        property: 'store',
      });
      const marketplaceId = salesOrderStore?.marketplaceId;
      const isFedexHal = ShipmentConfigurationService.isFedexHal(requestedService) || false;
      const pickupRelayLocation = Global.selectEntitiesFulfillmentPlanProperty(state, { fulfillmentPlanId, property: 'pickupLocation' });
      const CarrierIds = pickupRelayLocation?.pickupCarrierId ? [pickupRelayLocation.pickupCarrierId] : [];

      const { shipTo } = Global.selectCSWShippingConfiguration(state, { fulfillmentPlanId }) || {};

      const shipmentConfiguration = {
        shipFromId: updatedShipFromId,
        shipTo,
        isFedexHal,
        CarrierIds,
        marketplaceId,
      };

      return Api.getAvailableShippingServices(shipmentConfiguration).pipe(
        mergeMap((data) => {
          const updatedAvailableShippingServices = get(data, 'data');
          availableSellerShippingServicesSubject.next({
            fulfillmentPlanId,
            shippingServices: updatedAvailableShippingServices,
          });
          return of(Actions.updateAvailableShippingServices(fulfillmentPlanId, updatedAvailableShippingServices));
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const fulfillmentPlannerGlobalListeners = (action$) => {
  return action$.pipe(
    ofType(Actions.INIT_CSW_GLOBAL_LISTENERS),
    switchMap(({ fulfillmentPlanId, callback, bulkUpdateFinishedHandler }) => {
      return merge(
        action$.pipe(
          takeUntil(action$.pipe(ofType(Actions.UNBIND_CSW_GLOBAL_LISTENERS, take(1)))),
          ofType(Actions.SYNC_CSW_TO_ENTITIES),
          switchMap(() => {
            return action$.pipe(
              ofType(BulkloadActions.BULKLOAD_SUCCESS),
              take(1),
              mergeMap((action) => {
                const { fulfillmentPlans } = action?.entities ?? {};
                const foundEntity = fulfillmentPlans[fulfillmentPlanId];
                if (foundEntity) {
                  callback?.(foundEntity);
                }
                return EMPTY;
              }),
            );
          }),
        ),
        action$.pipe(
          takeUntil(action$.pipe(ofType(Actions.UNBIND_CSW_GLOBAL_LISTENERS, take(1)))),
          ofType(BulkUpdateActions.BULK_UPDATE_JOB_COMPLETE),
          switchMap(() => {
            bulkUpdateFinishedHandler();
            return EMPTY;
          }),
        ),
      );
    }),
  );
};

export const createTestLabel = (action$) => {
  return action$.pipe(
    ofType(Actions.CSW_CREATE_TEST_LABEL),
    mergeMap(({ fulfillmentPlanId }) => {
      return Api.createTestLabel(fulfillmentPlanId).pipe(
        mergeMap(() => {
          return of(Actions.createTestLabelSuccess(fulfillmentPlanId));
        }),
        catchError((errorResponse) => {
          const errorMessages = get(errorResponse, 'response.data.Errors', emptyArray).map(({ Message }) => Message);
          createLabelSubject.next({
            fulfillmentPlanId,
            isCreatingLabel: false,
            errorMessages,
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const createTestLabelSuccess = (action$) => {
  return action$.pipe(
    ofType(Actions.CSW_CREATE_TEST_LABEL_SUCCESS),
    mergeMap(({ fulfillmentPlanId }) => {
      const labelUrl = `/api/download/testlabel/fulfillmentplan/${fulfillmentPlanId}`;
      return of(PrintActions.openPrintModal(labelUrl, 'label', fulfillmentPlanId));
    }),
  );
};
